import React, {useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import Alert from "../../../../components/alert/alert.jsx";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputRadio from "../../../../components/input/input-radio.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";

const STEPS = {
    CONFIRM: "confirm",
    CLOSE_SUPPORTS: "closeSupports",
    CLOSE_MAIN: "closeMain",
    GENERATE_KM: "generateKm",
    KEEP_SUPPORTS: "keepSupports",
};

const POLICE_AUTHORITY_FLAG_KEYS = ["flagrant", "infracionalAct", "indicted", "notCriminal"];
const FLAG_LABELS = {
    notFound: "Não constatado",
    publicPatrimony: "Patrimônio público",
    policeAuthority: "Autoridade policial?",
    flagrant: "Flagrante",
    infracionalAct: "Ato infracional",
    indicted: "Indiciado",
    notCriminal: "Não criminal",
};

const STEP_TITLES = {
    [STEPS.CONFIRM]: "Mudar para principal?",
    [STEPS.CLOSE_SUPPORTS]: "Encerrar despachos de apoio",
    [STEPS.CLOSE_MAIN]: "Encerrar despacho principal",
    [STEPS.GENERATE_KM]: "Gerar despacho",
    [STEPS.KEEP_SUPPORTS]: "Permanecer com quais apoios?",
};

const ChangeOwnerModal = ({payload, boName = "BO", onClose, onSuccess}) => {
    const {configuration} = useDashboardContext();
    const [step, setStep] = useState(STEPS.CONFIRM);
    const [nature, setNature] = useState("");
    const [finalMileage, setFinalMileage] = useState("");
    const [providence, setProvidence] = useState("");
    const [finalObservation, setFinalObservation] = useState("");
    const [flags, setFlags] = useState({});
    const [generateMileage, setGenerateMileage] = useState("");
    const [closeSupportRows, setCloseSupportRows] = useState([]);
    const [keepSupportIds, setKeepSupportIds] = useState(() => new Set());
    const [keepMainAsSupport, setKeepMainAsSupport] = useState(false);
    const [mainMileageWarning, setMainMileageWarning] = useState(null);
    const [allowInvalidMainMileage, setAllowInvalidMainMileage] = useState(false);
    const [generateMileageWarning, setGenerateMileageWarning] = useState(null);
    const [allowInvalidGenerateMileage, setAllowInvalidGenerateMileage] = useState(false);
    const [isValidatingMileage, setIsValidatingMileage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const confirmationPayloadRef = useRef(null);
    const requested = Boolean(payload);
    const dispatchId = payload?.garrison?.dispatchId;

    const nameQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.changeOwnerName(dispatchId),
        queryFn: () => DispatchService.getGarrisonNameByDispatch(dispatchId),
        enabled: requested && Boolean(dispatchId),
    });

    const supportsQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.changeOwnerSupports(dispatchId),
        queryFn: () => DispatchService.getSupportsDispatchByChangeOwner(dispatchId),
        enabled: requested && Boolean(dispatchId) && step !== STEPS.CONFIRM,
    });
    const isReady = !nameQuery.isFetching && (nameQuery.isSuccess || nameQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) {
            confirmationPayloadRef.current = null;
            return;
        }
        setStep(STEPS.CONFIRM);
        setNature("");
        setFinalMileage("");
        setProvidence("");
        setFinalObservation("");
        setFlags({});
        setGenerateMileage("");
        setCloseSupportRows([]);
        setKeepSupportIds(new Set());
        setKeepMainAsSupport(false);
        setMainMileageWarning(null);
        setAllowInvalidMainMileage(false);
        setGenerateMileageWarning(null);
        setAllowInvalidGenerateMileage(false);
        setIsValidatingMileage(false);
        setIsSubmitting(false);
    }, [requested]);

    useEffect(() => {
        if (!open || step !== STEPS.CONFIRM || confirmationPayloadRef.current === payload) return;

        confirmationPayloadRef.current = payload;
        projectUtils.swalMessage(
            "warning",
            "Mudar para principal?",
            `Deseja mudar a guarnição ${nameQuery.data ?? payload?.garrison?.garrisonName ?? "selecionada"} para principal?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    setStep(STEPS.CLOSE_SUPPORTS);
                    return;
                }

                closeModal();
            },
            false,
        );
    }, [closeModal, nameQuery.data, open, payload, step]);

    useEffect(() => {
        if (!supportsQuery.data) return;
        const list = supportsQuery.data.listSupport ?? [];
        setCloseSupportRows(list.map(item => ({
            dispatchId: item.dispatchId,
            garrisonId: item.garrisonId,
            garrisonName: item.garrisonName,
            kmControl: Boolean(item.kmControl),
            nature: item.nature,
            finalMileage: configuration.isSuggestKm && item.kmControl && item.actualKm !== undefined && item.actualKm !== null
                ? String(item.actualKm)
                : "",
            finalObservation: "",
            cancelSupport: false,
            mileageWarning: null,
            allowInvalidMileage: false,
        })));
        setNature(supportsQuery.data.dispatchMain?.nature ?? "");
        const mainActualKm = supportsQuery.data.dispatchMain?.actualKm;
        setFinalMileage(configuration.isSuggestKm && supportsQuery.data.dispatchMain?.kmControl && mainActualKm !== undefined && mainActualKm !== null
            ? String(mainActualKm)
            : "");
    }, [configuration.isSuggestKm, supportsQuery.data]);

    const data = supportsQuery.data ?? {};
    const dispatchMain = data.dispatchMain ?? {};

    const natureOptions = useMemo(() => {
        const options = (data.natures ?? []).map(item => ({
            value: item.name ?? item.alias,
            label: item.alias ?? item.name,
            occurrence: item.occurrence,
        }));
        const currentNature = dispatchMain.nature;

        if (currentNature && !options.some(option => String(option.value) === String(currentNature))) {
            options.push({value: currentNature, label: currentNature});
        }

        return options;
    }, [data.natures, dispatchMain.nature]);

    const providenceOptions = useMemo(() => (data.providences ?? []).map(item => ({
        value: item.name ?? item.value ?? item,
        label: item.label ?? item.name ?? item,
    })), [data.providences]);

    const setFlag = (key, value) => setFlags(prev => {
        if (key === "notFound" && value) {
            return {
                ...prev,
                notFound: true,
                publicPatrimony: false,
                policeAuthority: false,
                flagrant: false,
                infracionalAct: false,
                indicted: false,
                notCriminal: false,
            };
        }

        if (key === "publicPatrimony" && value) {
            return {...prev, publicPatrimony: true, notFound: false};
        }

        if (key === "policeAuthority") {
            if (value) return {...prev, policeAuthority: true, notFound: false};

            return {
                ...prev,
                policeAuthority: false,
                flagrant: false,
                infracionalAct: false,
                indicted: false,
                notCriminal: false,
            };
        }

        if (POLICE_AUTHORITY_FLAG_KEYS.includes(key) && value) {
            return {...prev, [key]: true, policeAuthority: true, notFound: false};
        }

        return {...prev, [key]: value};
    });
    const updateSupport = (index, partial) => setCloseSupportRows(prev => prev.map((row, i) => i === index ? {...row, ...partial} : row));
    const toggleKeepSupport = (garrisonId, checked) => setKeepSupportIds(prev => {
        const next = new Set(prev);
        if (checked) next.add(garrisonId); else next.delete(garrisonId);
        return next;
    });
    const supportToGenerate = closeSupportRows.find(row => String(row.garrisonId) === String(data.garrisonId));
    const generateKmControl = Boolean(supportToGenerate?.kmControl);
    const remainingSupportRows = closeSupportRows.filter(row => String(row.dispatchId) !== String(dispatchId));
    const allRemainingSupportsSelected = remainingSupportRows.length > 0
        && remainingSupportRows.every(row => keepSupportIds.has(row.garrisonId));
    const unuploadedBoMessage = useMemo(() => {
        const occurrenceNumber = dispatchMain.occurrenceNumber;
        if (dispatchMain.hasMobile === true && occurrenceNumber !== undefined && occurrenceNumber !== "" && dispatchMain.boSend === false) {
            return `O ${boName} ${occurrenceNumber} ainda não foi enviado`;
        }

        const hasUnuploadedSupportBo = (data.listSupport ?? []).some(item =>
            item.hasMobile === true
            && item.occurrenceNumber !== undefined
            && item.occurrenceNumber !== ""
            && item.boSend === false
        );

        return hasUnuploadedSupportBo ? `Pelo menos um apoio possui ${boName} que ainda não foi enviado` : "";
    }, [boName, data.listSupport, dispatchMain.boSend, dispatchMain.hasMobile, dispatchMain.occurrenceNumber]);

    const toggleAllKeepSupports = checked => {
        setKeepSupportIds(checked ? new Set(remainingSupportRows.map(row => row.garrisonId)) : new Set());
    };

    const openGenerateMileageStep = () => {
        setGenerateMileage(configuration.isSuggestKm && generateKmControl ? supportToGenerate?.finalMileage ?? "" : "");
        setGenerateMileageWarning(null);
        setAllowInvalidGenerateMileage(false);
        setStep(STEPS.GENERATE_KM);
    };

    const validateCloseSupportsStep = async () => {
        const warnings = await Promise.all(closeSupportRows.map(async row => {
            if (!row.kmControl || row.finalMileage === "") return null;

            const result = await DispatchService.checkMileage(row.garrisonId, row.finalMileage);
            return result?.invalid && !row.allowInvalidMileage
                ? {actualKm: result.actualKm ?? "0"}
                : null;
        }));

        setCloseSupportRows(prev => prev.map((row, index) => ({
            ...row,
            mileageWarning: warnings[index] ?? row.mileageWarning,
        })));

        return warnings.every(warning => warning === null);
    };

    const validateMainStep = async () => {
        if (!nature) {
            projectUtils.swalErrorMessage("", "Selecione a natureza de encerramento do despacho principal.", "Ok");
            return false;
        }

        if (!dispatchMain.kmControl || finalMileage === "") return true;

        const result = await DispatchService.checkMileage(dispatchMain.garrisonId, finalMileage);
        if (!result?.invalid || allowInvalidMainMileage) return true;

        setMainMileageWarning({actualKm: result.actualKm ?? "0"});
        return false;
    };

    const validateGenerateMileageStep = async () => {
        if (!generateKmControl || generateMileage === "") return true;

        const result = await DispatchService.checkMileage(data.garrisonId, generateMileage);
        if (!result?.invalid || allowInvalidGenerateMileage) return true;

        setGenerateMileageWarning({actualKm: result.actualKm ?? "0"});
        return false;
    };

    const advanceAfterValidation = async (validation, onValid) => {
        setIsValidatingMileage(true);

        try {
            if (await validation()) onValid();
        } catch (error) {
            console.error("Erro ao validar a quilometragem da mudança de principal:", error);
            projectUtils.swalErrorMessage("Erro", "Não foi possível validar a quilometragem informada.", "Ok");
        } finally {
            setIsValidatingMileage(false);
        }
    };

    const renderMileageWarning = ({id, warning, allowInvalidMileage, onChange}) => {
        if (!warning) return null;

        return (
            <Alert target="danger" className="text-sm">
                <div className="mb-2 text-center">
                    O km informado é menor que o atual. Deseja continuar?<br/>
                    Km atual: <b>{warning.actualKm}</b>
                </div>
                <div className="flex justify-center gap-6">
                    <InputRadio id={`${id}-no`} name={id} label="Não" checked={!allowInvalidMileage} onChange={() => onChange(false)}/>
                    <InputRadio id={`${id}-yes`} name={id} label="Sim" checked={allowInvalidMileage} onChange={() => onChange(true)}/>
                </div>
            </Alert>
        );
    };

    const handleFinish = async () => {
        setIsSubmitting(true);

        try {
            const supports = closeSupportRows.map(row => ({
                garrisonId: row.garrisonId,
                dispatchId: row.dispatchId,
                finalMileage: row.finalMileage || undefined,
                finalObservation: row.finalObservation || undefined,
                cancelSupport: row.cancelSupport,
                nature: row.nature,
            }));

            const jsonData = {
                closedOwnerGarrison: {
                    garrisonId: dispatchMain.garrisonId,
                    dispatchId: dispatchMain.dispatchId,
                    finalMileage: finalMileage || undefined,
                    providence,
                    nature,
                    finalObservation,
                    cancelSupport: false,
                    flagrant: !!flags.flagrant,
                    infracionalAct: !!flags.infracionalAct,
                    publicPatrimony: !!flags.publicPatrimony,
                    notFound: !!flags.notFound,
                    policeAuthority: !!flags.policeAuthority,
                    indicted: !!flags.indicted,
                    notCriminal: !!flags.notCriminal,
                    supports,
                },
                generateDispatchByGarrison: {
                    garrisonId: data.garrisonId,
                    attendanceId: data.attendanceId,
                    mileage: generateMileage || undefined,
                },
                generateDispatchByGarrisonSupport: {
                    garrisonSupports: remainingSupportRows
                        .filter(row => keepSupportIds.has(row.garrisonId))
                        .map(row => ({garrisonId: row.garrisonId, nature: row.nature})),
                    mainDispatchSupport: keepMainAsSupport,
                },
            };

            await DispatchService.changeOwner(jsonData);
            onSuccess();
            projectUtils.swalSuccessMessage("success", "Sucesso", "Mudança realizada com sucesso.", "Ok");
        } catch (error) {
            console.error("Erro ao mudar apoio para principal pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível mudar o despacho principal";
            projectUtils.swalErrorMessage("Erro", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const isLoadingSupports = step !== STEPS.CONFIRM && supportsQuery.isLoading;
    const hasSupportsError = step !== STEPS.CONFIRM && supportsQuery.isError;

    const renderFooter = () => {
        const disableNav = isLoadingSupports || hasSupportsError || isValidatingMileage;

        if (step === STEPS.CLOSE_SUPPORTS) {
            return (
                <>
                    <Button type="button" target="secondary" onClick={() => setStep(STEPS.CONFIRM)} disabled={isValidatingMileage}>Voltar</Button>
                    <Button type="button" onClick={() => advanceAfterValidation(validateCloseSupportsStep, () => setStep(STEPS.CLOSE_MAIN))} loading={isValidatingMileage} disabled={disableNav}>Próximo</Button>
                </>
            );
        }

        if (step === STEPS.CLOSE_MAIN) {
            return (
                <>
                    <Button type="button" target="secondary" onClick={() => setStep(STEPS.CLOSE_SUPPORTS)} disabled={isValidatingMileage}>Voltar</Button>
                    <Button type="button" onClick={() => advanceAfterValidation(validateMainStep, openGenerateMileageStep)} loading={isValidatingMileage} disabled={disableNav}>Próximo</Button>
                </>
            );
        }

        if (step === STEPS.GENERATE_KM) {
            return (
                <>
                    <Button type="button" target="secondary" onClick={() => setStep(STEPS.CLOSE_MAIN)} disabled={isValidatingMileage}>Voltar</Button>
                    <Button type="button" onClick={() => advanceAfterValidation(validateGenerateMileageStep, () => setStep(STEPS.KEEP_SUPPORTS))} loading={isValidatingMileage} disabled={disableNav}>Próximo</Button>
                </>
            );
        }

        return (
            <>
                <Button type="button" target="secondary" onClick={() => setStep(STEPS.GENERATE_KM)} disabled={isSubmitting}>Voltar</Button>
                <Button type="button" onClick={handleFinish} loading={isSubmitting} disabled={disableNav} actionFeedbackType="save" loadingText="Concluindo..." successText="Concluído com sucesso">Concluir</Button>
            </>
        );
    };

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open && step !== STEPS.CONFIRM} setOpen={closeModal} title={STEP_TITLES[step]} className="w-full max-w-2xl" isCloseClick={false} animated={false}>
                <Modal.Body className="relative flex max-h-[calc(100vh-180px)] flex-col gap-4 overflow-auto">
                    {open && isLoadingSupports && <Loader/>}
                    {hasSupportsError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar os dados dos apoios.</div>}

                    {step === STEPS.CLOSE_SUPPORTS && !isLoadingSupports && !hasSupportsError && (
                        <>
                            {unuploadedBoMessage && <Alert target="warning" className="text-sm">{unuploadedBoMessage}</Alert>}
                            {closeSupportRows.length === 0
                                ? <p className="text-sm text-[#555]">Nenhum apoio vinculado para encerrar.</p>
                                : closeSupportRows.map((row, index) => (
                                    <div key={row.dispatchId} className="flex flex-col gap-2 rounded border border-[#ededed] p-3 space-y-2">
                                        <span className="text-sm font-semibold text-[#555]">{row.garrisonName}</span>
                                        {row.kmControl && <InputForm integerOnly label="Km final" type="number" min={1} value={row.finalMileage} onChange={({target}) => updateSupport(index, {finalMileage: target.value, mileageWarning: null, allowInvalidMileage: false})}/>}
                                        <InputTextarea label="Observação final" value={row.finalObservation} onChange={({target}) => updateSupport(index, {finalObservation: target.value})} height={90}/>
                                        <InputCheckbox id={`change-owner-cancel-${row.dispatchId}`} label="Cancelar apoio" checked={row.cancelSupport} onChange={({target}) => updateSupport(index, {cancelSupport: target.checked})}/>
                                        {renderMileageWarning({
                                            id: `change-owner-support-mileage-${row.dispatchId}`,
                                            warning: row.mileageWarning,
                                            allowInvalidMileage: row.allowInvalidMileage,
                                            onChange: value => updateSupport(index, {allowInvalidMileage: value}),
                                        })}
                                    </div>
                                ))}
                        </>
                    )}

                    {step === STEPS.CLOSE_MAIN && !isLoadingSupports && !hasSupportsError && (
                        <>
                            <div><p className="text-[14px] font-bold text-[#676a6c]">Guarnição principal</p><span className="text-sm text-[#555]">{dispatchMain.garrisonName}</span></div>
                            <SelectComponent classNameWrapper="w-full" label="Natureza *" value={nature} options={natureOptions} onChange={selected => setNature(selected?.value ?? "")}/>
                            {Boolean(dispatchMain.kmControl) && <InputForm integerOnly label="Km final" type="number" min={1} value={finalMileage} onChange={({target}) => { setFinalMileage(target.value); setMainMileageWarning(null); setAllowInvalidMainMileage(false); }}/>}
                            <InputTextarea label="Observação final" value={finalObservation} onChange={({target}) => setFinalObservation(target.value)} height={110}/>
                            <SelectComponent classNameWrapper="w-full" label="Providência" value={providence} options={providenceOptions} onChange={selected => setProvidence(selected?.value ?? "")}/>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {["notFound", "publicPatrimony"].map(key => (
                                    <InputCheckbox key={key} id={`change-owner-${key}`} label={FLAG_LABELS[key]} checked={!!flags[key]} onChange={({target}) => setFlag(key, target.checked)}/>
                                ))}
                                <div className="col-span-2">
                                    <InputCheckbox id="change-owner-policeAuthority" label={FLAG_LABELS.policeAuthority} checked={!!flags.policeAuthority} onChange={({target}) => setFlag("policeAuthority", target.checked)}/>
                                </div>
                                <div className="col-span-2 ml-6 grid grid-cols-2 gap-2">
                                    {["infracionalAct", "notCriminal", "flagrant", "indicted"].map(key => (
                                        <InputCheckbox key={key} id={`change-owner-${key}`} label={FLAG_LABELS[key]} checked={!!flags[key]} onChange={({target}) => setFlag(key, target.checked)}/>
                                    ))}
                                </div>
                            </div>
                            {renderMileageWarning({
                                id: "change-owner-main-mileage",
                                warning: mainMileageWarning,
                                allowInvalidMileage: allowInvalidMainMileage,
                                onChange: setAllowInvalidMainMileage,
                            })}
                        </>
                    )}

                    {step === STEPS.GENERATE_KM && !isLoadingSupports && !hasSupportsError && (
                        <>
                            <div>
                                <p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p>
                                <span className="text-sm text-[#555]">{supportToGenerate?.garrisonName}</span>
                            </div>
                            {generateKmControl && <InputForm integerOnly label="Km inicial do novo despacho" type="number" min={1} value={generateMileage} onChange={({target}) => { setGenerateMileage(target.value); setGenerateMileageWarning(null); setAllowInvalidGenerateMileage(false); }}/>}
                            {renderMileageWarning({
                                id: "change-owner-generate-mileage",
                                warning: generateMileageWarning,
                                allowInvalidMileage: allowInvalidGenerateMileage,
                                onChange: setAllowInvalidGenerateMileage,
                            })}
                        </>
                    )}

                    {step === STEPS.KEEP_SUPPORTS && !isLoadingSupports && !hasSupportsError && (
                        <div className="flex flex-col gap-2">
                            {remainingSupportRows.length > 0 && (
                                <>
                                    <InputCheckbox id="change-owner-keep-all" label="Selecionar todos" checked={allRemainingSupportsSelected} onChange={({target}) => toggleAllKeepSupports(target.checked)}/>
                                    <div className="flex flex-col gap-2 rounded border border-[#ededed] p-3">
                                        {remainingSupportRows.map(row => (
                                            <InputCheckbox key={row.garrisonId} id={`change-owner-keep-${row.garrisonId}`} label={row.garrisonName} checked={keepSupportIds.has(row.garrisonId)} onChange={({target}) => toggleKeepSupport(row.garrisonId, target.checked)}/>
                                        ))}
                                    </div>
                                </>
                            )}
                            <InputCheckbox id="change-owner-keep-main" label="Manter guarnição principal como apoio?" checked={keepMainAsSupport} onChange={({target}) => setKeepMainAsSupport(target.checked)}/>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {renderFooter()}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ChangeOwnerModal;